import * as THREE from "three";
import type { NormalizedScreenPoint } from "@/runtime/types";

const ENGINE_PATH = "external/xr/1.0.0/xr.js";
const ENGINE_SCRIPT_ID = "eighth-wall-engine";

interface Vector3Like { x: number; y: number; z: number }
interface QuaternionLike { x: number; y: number; z: number; w: number }
export interface HitResult {
  type?: string;
  position: Vector3Like | [number, number, number];
  rotation?: QuaternionLike | [number, number, number, number];
  quaternion?: QuaternionLike | [number, number, number, number];
  scale?: number;
}

export interface PipelineModule {
  name: string;
  onStart?: () => void;
  onUpdate?: (event: PipelineUpdateEvent) => void;
  onCameraStatusChange?: (event: { status?: string; error?: unknown }) => void;
}

export interface PipelineUpdateEvent {
  processCpuResult?: {
    reality?: {
      trackingStatus?: string;
      trackingReason?: string;
    };
  };
  frameStartResult?: { timestamp?: number };
}

export interface EighthWallEngine {
  loadChunk?: (name: string) => Promise<unknown>;
  start?: () => Promise<void> | void;
  stop?: () => Promise<void> | void;
  pause?: () => Promise<void> | void;
  resume?: () => Promise<void> | void;
  destroy?: () => Promise<void> | void;
  hitTest?: (point: NormalizedScreenPoint) => Promise<HitResult | HitResult[] | null> | HitResult | HitResult[] | null;
  run?: (options: { canvas: HTMLCanvasElement; allowedDevices?: unknown }) => Promise<void> | void;
  addCameraPipelineModules?: (modules: PipelineModule[]) => void;
  removeCameraPipelineModule?: (name: string) => void;
  GlTextureRenderer?: { pipelineModule(): PipelineModule };
  Threejs?: {
    pipelineModule(): PipelineModule;
    xrScene(): { scene: THREE.Scene; camera: THREE.PerspectiveCamera; renderer: THREE.WebGLRenderer };
  };
  XrController?: {
    configure(options: Record<string, unknown>): void;
    pipelineModule(): PipelineModule;
    hitTest(x: number, y: number): HitResult[];
    recenter(): void;
    updateCameraProjectionMatrix?(options: { origin: THREE.Vector3; facing: THREE.Quaternion }): void;
  };
  XrConfig?: { device(): { ANY?: unknown } };
}

type EngineWindow = Window & typeof globalThis & { XR8?: EighthWallEngine; THREE?: typeof THREE };

let sharedEnginePromise: Promise<EighthWallEngine> | null = null;

export function attachThreeGlobal(): void {
  (window as EngineWindow).THREE = THREE;
}

export function clearThreeGlobal(): void {
  const engineWindow = typeof window === "undefined" ? null : window as EngineWindow;
  if (engineWindow?.THREE === THREE) delete engineWindow.THREE;
}

export function loadEighthWallEngine(engineUrl = `${import.meta.env.BASE_URL}${ENGINE_PATH}`): Promise<EighthWallEngine> {
  if (sharedEnginePromise) return sharedEnginePromise;
  sharedEnginePromise = new Promise<EighthWallEngine>((resolve, reject) => {
    const engineWindow = window as EngineWindow;
    if (engineWindow.XR8) {
      resolve(engineWindow.XR8);
      return;
    }

    const finish = () => {
      if (!engineWindow.XR8) return;
      cleanup();
      resolve(engineWindow.XR8);
    };
    const fail = () => {
      cleanup();
      sharedEnginePromise = null;
      reject(new Error("The self-hosted 8th Wall engine failed to load."));
    };
    const cleanup = () => {
      window.removeEventListener("xrloaded", finish);
      window.clearTimeout(timeout);
    };
    const timeout = window.setTimeout(fail, 20_000);
    window.addEventListener("xrloaded", finish);

    let script = document.getElementById(ENGINE_SCRIPT_ID) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement("script");
      script.id = ENGINE_SCRIPT_ID;
      script.src = new URL(engineUrl, document.baseURI).href;
      script.async = true;
      script.dataset.preloadChunks = "slam";
      script.addEventListener("error", fail, { once: true });
      document.head.append(script);
    }
  });
  return sharedEnginePromise;
}

export function selectBestHit(results: HitResult[]): HitResult | null {
  const priority = new Map([
    ["DETECTED_SURFACE", 0],
    ["ESTIMATED_SURFACE", 1],
    ["FEATURE_POINT", 2],
  ]);
  return results
    .filter((result) => result.type !== "UNSPECIFIED")
    .sort((left, right) => (priority.get(left.type ?? "") ?? 3) - (priority.get(right.type ?? "") ?? 3))[0] ?? null;
}

export function tuple3(value: HitResult["position"]): [number, number, number] {
  return Array.isArray(value) ? [value[0], value[1], value[2]] : [value.x, value.y, value.z];
}

export function tuple4(value?: HitResult["quaternion"]): [number, number, number, number] {
  if (!value) return [0, 0, 0, 1];
  return Array.isArray(value) ? [value[0], value[1], value[2], value[3]] : [value.x, value.y, value.z, value.w];
}

export function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

export function errorMessage(cause: unknown, fallback: string): string {
  return cause instanceof Error ? cause.message : fallback;
}
